import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { NavigationService } from '@core/navigation/navigation.service';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { AuthService } from 'app/core/auth/auth.service';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { EMPTY, Observable, catchError, throwError } from 'rxjs';

/**
 * Intercept
 *
 * @param req
 * @param next
 */
export const authInterceptor = (req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> => {
    const authService = inject(AuthService);
    const router = inject(Router);
    const matDialog = inject(MatDialog);
    const navigationService = inject(NavigationService);
    const confirmService = inject(FuseConfirmationService);
    const token = authService.accessToken;
    const isEmbedContext = authService.isEmbedContext();
    const hasPrimaryToken = !!token;

    let newReq = req.clone();
    const requestUrl = String(req.url || '').toLowerCase();

    const forceSignOut = (title: string, message: string): void => {
        authService.signOut({
            type: 'warning',
            title,
            message,
            duration: 6,
        });

        matDialog.closeAll();

        if (!router.url.startsWith('/sign-in')) {
            void router.navigate(['/sign-in']);
        }
    };

    if (token) {
        const tokenValido = AuthUtils.isValidJwt(token) && !AuthUtils.isTokenExpired(token);
        if (!tokenValido) {
            forceSignOut('Sesion finalizada', 'Tu sesion no es valida o expiro. Inicia sesion nuevamente.');
            return EMPTY;
        }
    }

    if (token && AuthUtils.isValidJwt(token) && !AuthUtils.isTokenExpired(token)) {
        newReq = req.clone({
            headers: req.headers.set('Authorization', 'Bearer ' + token),
        });

        if (!isEmbedContext && navigationService._navigationMenuData.length === 0) {
            void navigationService.getMenus();
        }
    }

    const mostrarDialogoError = (error: HttpErrorResponse): void => {
        let descripcion = error.error?.error || error.error?.message || error.error?.result?.descripcion || 'Ocurrio un error desconocido.';
        const errores = error.error?.result?.errores || {};

        if (typeof descripcion === 'object' && descripcion !== null) {
            descripcion = descripcion.message || descripcion.error || JSON.stringify(descripcion);
        } else if (typeof descripcion === 'string') {
            const trimmed = descripcion.trim();
            if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
                try {
                    const parsed = JSON.parse(trimmed);
                    descripcion = parsed.message || parsed.error || descripcion;
                } catch (e) {
                    // No hacer nada
                }
            }
        }

        let mensajeError = `${error.message}\n${descripcion}`;
        for (const [campo, mensajes] of Object.entries(errores)) {
            if (Array.isArray(mensajes)) {
                mensajeError += `\n${campo}: ${mensajes.join(', ')}`;
            }
        }

        confirmService.open({
            title: `${error.name} ${error.status}` || 'Error',
            message: mensajeError,
            dismissible: false,
            actions: {
                confirm: {
                    show: true,
                    color: 'warn',
                    label: 'Confirmar',
                },
                cancel: {
                    show: false,
                },
            },
        });
    };

    const isMalformedJwtServerError = (error: HttpErrorResponse): boolean => {
        if (error.status !== 500) {
            return false;
        }

        const backendMessage = String(error.error?.message ?? error.error?.result?.descripcion ?? '').toLowerCase();
        const backendFile = String(error.error?.file ?? '').toLowerCase();
        const stackTrace = Array.isArray(error.error?.['stack-trace'])
            ? error.error['stack-trace'].join(' ').toLowerCase()
            : '';
        const serializedBody = JSON.stringify(error.error ?? {}).toLowerCase();

        return backendMessage.includes('malformed json')
            || backendFile.includes('php-jwt')
            || stackTrace.includes('firebase\\jwt\\jwt::decode')
            || stackTrace.includes('jwt.php')
            || serializedBody.includes('malformed json')
            || serializedBody.includes('firebase\\\\jwt')
            || serializedBody.includes('jwt.php');
    };

    const isMenuRequestServerAuthFailure = (error: HttpErrorResponse): boolean => {
        if (error.status !== 500) {
            return false;
        }

        const requestUrl = String(error.url ?? newReq.url ?? '').toLowerCase();
        return requestUrl.includes('com_menus') || requestUrl.includes('view_alertas');
    };

    const isSilentBackgroundRequest = (url: string): boolean => {
        return url.includes('view_alertas');
    };

    return next(newReq).pipe(
        catchError((error) => {
            if (!(error instanceof HttpErrorResponse)) {
                return throwError(() => error);
            }

            if (error.status === 401) {
                if (isEmbedContext && !hasPrimaryToken) {
                    return EMPTY;
                }
                forceSignOut('Sesion expirada', 'Tu sesion expiro o fue invalidada. Inicia sesion nuevamente.');
                return EMPTY;
            }

            if (isMalformedJwtServerError(error) || isMenuRequestServerAuthFailure(error)) {
                if (isEmbedContext && !hasPrimaryToken) {
                    return EMPTY;
                }
                forceSignOut('Sesion invalida', 'No pudimos validar tu sesion. Inicia sesion nuevamente.');
                return EMPTY;
            }

            const errorUrl = String(error.url ?? requestUrl).toLowerCase();

            if (isSilentBackgroundRequest(errorUrl)) {
                return throwError(() => error);
            }

            if (error.status === 0 || error.status === 500 || error.status === 404) {
                mostrarDialogoError(error);
                return throwError(() => error);
            }

            mostrarDialogoError(error);
            return throwError(() => error);
        }),
    );
};
